import { useMemo } from "react";
import { useMandatorContext } from "@bn/theming";
import { useTopTopicsByIsin } from "@api/boersennews";
import { useMostDiscussedInstruments } from "@api/wso";
import { useGetInvestments } from "@api/v1/hooks/useInvestments";
import { useDashboardSelectedGroupsContext } from "@features/dashboard/DashboardSelectedGroupProvider";
import { useSelectedInvestments } from "@features/dashboard/hooks/useSelectedInvestments";

export interface CommunityTopic {
  title: string;
  target: string;
  perfRel?: number;
}

const useBoersennewsTopics = (
  limit: number,
  enabled: boolean
): { topics: CommunityTopic[] | undefined; isLoading: boolean } => {
  const { accountIds, isFetching: isAccountsFetching } =
    useDashboardSelectedGroupsContext();

  const { data: investmentsResponse, isLoading: isInvestmentsLoading } =
    useGetInvestments(
      { accountId: accountIds },
      { enabled: enabled && Boolean(accountIds?.length) }
    );

  const isins = useMemo(
    () =>
      Array.from(
        new Set(
          investmentsResponse?.investments
            ?.filter((inv) => inv.hasValidIsin)
            .map((inv) => inv.isin) ?? []
        )
      ),
    [investmentsResponse]
  );

  const { data, isLoading: isTopicsLoading } = useTopTopicsByIsin(
    isins,
    limit,
    enabled
  );

  const isLoading =
    enabled &&
    (isAccountsFetching ||
      accountIds === undefined ||
      (Boolean(accountIds.length) && isInvestmentsLoading) ||
      (isins.length > 0 && isTopicsLoading));

  return { topics: data, isLoading };
};

const useWsoTopics = (
  limit: number,
  enabled: boolean
): { topics: CommunityTopic[] | undefined; isLoading: boolean } => {
  const { data, isLoading } = useMostDiscussedInstruments({ enabled });
  const topics = data?.slice(0, limit).map((item) => ({
    title: item.displayName,
    target: item.link,
  }));
  return { topics, isLoading };
};

const useArivaTopics = (
  limit: number,
  enabled: boolean
): { topics: CommunityTopic[] | undefined; isLoading: boolean } => {
  const { accountIds, uniqueIsins, isAccountsFetching, isInvestmentsLoading } =
    useSelectedInvestments(enabled);

  const { data, isLoading: isTopicsLoading } = useTopTopicsByIsin(
    uniqueIsins,
    limit,
    enabled
  );

  const isLoading =
    enabled &&
    (isAccountsFetching ||
      accountIds === undefined ||
      (Boolean(accountIds.length) && isInvestmentsLoading) ||
      (uniqueIsins.length > 0 && isTopicsLoading));

  return { topics: data, isLoading };
};

export const useCommunityPosts = (limit: number) => {
  const { isWso, isAriva, isBoersenews } = useMandatorContext();
  const boersennews = useBoersennewsTopics(limit, isBoersenews);
  const wso = useWsoTopics(limit, isWso);
  const ariva = useArivaTopics(limit, isAriva);

  const { topics, isLoading } = isWso ? wso : isAriva ? ariva : boersennews;

  return {
    topics,
    isLoading,
    isEmpty: !isLoading && (topics?.length ?? 0) === 0,
  };
};
